/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Box, Button, Chip, Fade, Grow, IconButton, InputBase, Paper, Tooltip, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import MAVEN from './mavenConfig';
import { askQuestion, verifyAnswer } from './mavenApi';
import { getPageContext } from './mavenPageContext';
import { buildHistory } from './mavenParse';
import panelTheme from './mavenTheme';
import MavenAnswer from './MavenAnswer';
import MavenAvatar from './MavenAvatar';
import MavenMessage from './MavenMessage';
import MavenWorking from './MavenWorking';
import UserMessage from './UserMessage';
import {
  AIADMIN_BORDER_DEFAULT_COLOR,
  AIADMIN_ON_PRIMARY_MUTED_TEXT_COLOR,
  AIADMIN_PRIMARY_COLOR,
  AIADMIN_PRIMARY_HOVER_COLOR,
  AIADMIN_SECONDARY_TEXT_COLOR,
  AIADMIN_SURFACE_WHITE_HEX_COLOR,
  AIADMIN_TEXT_PRIMARY,
  AIADMIN_TEXT_SECONDARY
} from '../../common/styles/aiadminThemeTokens';

// A floating chat window above the Maven button, not a full-height drawer.
const PANEL_WIDTH = 420;
const PANEL_MAX_HEIGHT = 680;
const PANEL_EXPANDED_WIDTH = 880;
// Keeps the window's top below the AI Admin header (about 64px) with some breathing room.
const APP_HEADER_SPACE = 80;
// Critically damped feel: quick start, soft settle, no overshoot (the window is not thrown, so no bounce).
const PANEL_EASING = 'cubic-bezier(0.2, 0.9, 0.3, 1)';
// How long "Undo" stays after starting a new chat: a quick undo instead of an "Are you sure?" dialog.
const UNDO_NEW_CHAT_MS = 6000;
/**
 * Right side panel: question box, example questions and the answers from this session (memory only).
 * Each question is a fresh graph run that carries the last few turns (for follow-ups); the answer is then
 * checked by ai-admin-services.
 * Page context: Studio passes `pageContext` (the open graph); AI Admin screens pass `page`, and the
 * screen's saved filters are added when the question is sent (see mavenPageContext).
 */
const MavenDrawer = ({ open, onClose, pageContext, page, bottom, right }) => {
  // The window takes the Maven button's place (the button is hidden while the window is open).
  const panelBottom = bottom;
  const [question, setQuestion] = useState('');
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [clearedItems, setClearedItems] = useState(null);
  const abortRef = useRef(null);
  const listEndRef = useRef(null);
  const inputRef = useRef(null);

  const updateItem = useCallback((id, changes) => {
    setItems((list) => list.map((item) => (item.id === id ? { ...item, ...changes } : item)));
  }, []);

  useEffect(() => {
    listEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [items.length]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!clearedItems) return undefined;
    const timer = setTimeout(() => setClearedItems(null), UNDO_NEW_CHAT_MS);
    return () => clearTimeout(timer);
  }, [clearedItems]);

  /** Back to the fresh intro, as if the window was just opened. Stops an answer that is still coming. */
  const startNewChat = useCallback(() => {
    abortRef.current?.abort();
    setRunning(false);
    setClearedItems(
      items.map((item) =>
        item.running ? { ...item, running: false, error: 'Stopped when you started a new chat.' } : item
      )
    );
    setItems([]);
    setQuestion('');
    inputRef.current?.focus();
  }, [items]);

  const undoNewChat = () => {
    setItems(clearedItems);
    setClearedItems(null);
  };

  const ask = useCallback(
    async (text) => {
      const q = text.trim();
      if (!q || running) return;
      if (!MAVEN.graphId) {
        setItems((list) => [
          ...list,
          { id: Date.now(), question: q, error: 'Set graphId in mavenConfig.js first.', steps: [] }
        ]);
        return;
      }
      const id = Date.now();
      setClearedItems(null);
      setItems((list) => [...list, { id, question: q, steps: [], answer: '', running: true }]);
      setQuestion('');
      setRunning(true);
      abortRef.current = new AbortController();
      try {
        const { answer, requestId } = await askQuestion({
          question: q,
          pageContext: pageContext || getPageContext(page),
          history: buildHistory(items, { turns: MAVEN.historyTurns, answerChars: MAVEN.historyAnswerChars }),
          signal: abortRef.current.signal,
          // Show the answer as it arrives, not only when it is complete.
          onProgress: ({ steps, text: streamText }) => updateItem(id, { steps, streamText })
        });
        updateItem(id, { answer, running: false, check: { loading: true } });
        try {
          updateItem(id, { check: { result: await verifyAnswer(answer, requestId) } });
        } catch {
          updateItem(id, { check: { error: true } });
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
        updateItem(id, { running: false, error: 'Sorry, something went wrong on my side. Please try again.' });
        setQuestion(q);
      } finally {
        setRunning(false);
      }
    },
    [running, items, pageContext, page, updateItem]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    ask(question);
  };

  // Enter sends, Shift+Enter adds a new line (as in Messages). Ignore Enter while an IME is composing.
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      ask(question);
    }
  };

  const canSend = !running && Boolean(question.trim());

  return (
    <ThemeProvider theme={panelTheme}>
      {/* The outer box holds the window's place and size (and animates expand / shrink). Inside it, the
          window grows out of the Maven button (bottom-right origin) and shrinks back into it on close.
          Kept mounted while hidden so this session's answers stay. No scrim: the dashboard stays usable. */}
      <Box
        sx={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.modal,
          right: { xs: 0, sm: right },
          bottom: { xs: 0, sm: panelBottom },
          // Normal: a compact chat window. Expanded: wider and as tall as the space below the app header.
          width: { xs: '100%', sm: expanded ? `min(${PANEL_EXPANDED_WIDTH}px, calc(100vw - 48px))` : PANEL_WIDTH },
          height: {
            xs: '100%',
            sm: expanded
              ? `calc(100vh - ${panelBottom + APP_HEADER_SPACE}px)`
              : `min(${PANEL_MAX_HEIGHT}px, calc(100vh - ${panelBottom + APP_HEADER_SPACE}px))`
          },
          // While closed, clicks go through to the page underneath.
          pointerEvents: open ? 'auto' : 'none',
          transition: `width 260ms ${PANEL_EASING}, height 260ms ${PANEL_EASING}`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
        }}
      >
        <Grow
          in={open}
          timeout={{ enter: 280, exit: 180 }}
          easing={{ enter: PANEL_EASING, exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
          style={{ transformOrigin: 'bottom right' }}
          onEntered={() => inputRef.current?.focus()}
        >
          <Paper
            role="dialog"
            aria-label={MAVEN.name}
            elevation={0}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
            }}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: { xs: 0, sm: '20px' },
              border: `1px solid ${AIADMIN_BORDER_DEFAULT_COLOR}`,
              // A large floating surface reads as "thicker": a soft, deep shadow, not a hard outline.
              boxShadow: '0 24px 48px rgba(26, 32, 44, 0.18), 0 4px 12px rgba(26, 32, 44, 0.08)',
              '@media (prefers-reduced-motion: reduce)': { transform: 'none !important' }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Navy header, like the dashboard cards and app header. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  bgcolor: AIADMIN_PRIMARY_COLOR,
                  color: AIADMIN_TEXT_SECONDARY
                }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  variant="dot"
                  sx={{ '& .MuiBadge-dot': { bgcolor: '#22c55e', boxShadow: `0 0 0 2px ${AIADMIN_PRIMARY_COLOR}` } }}
                >
                  <MavenAvatar size={36} light />
                </Badge>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.01em', color: 'inherit' }}
                  >
                    {MAVEN.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: AIADMIN_ON_PRIMARY_MUTED_TEXT_COLOR }}>
                    {MAVEN.tagline}
                  </Typography>
                </Box>
                <Tooltip title="New chat">
                  <span>
                    <IconButton
                      aria-label="Start a new chat"
                      onClick={startNewChat}
                      disabled={!items.length}
                      sx={{ color: 'inherit', '&.Mui-disabled': { color: 'inherit', opacity: 0.4 } }}
                    >
                      <AddCommentOutlinedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                {/* Expand / shrink is only offered where there is room (on phones the window is already full screen). */}
                <Tooltip title={expanded ? 'Shrink' : 'Expand'}>
                  <IconButton
                    aria-label={expanded ? `Shrink ${MAVEN.name}` : `Expand ${MAVEN.name}`}
                    aria-pressed={expanded}
                    onClick={() => setExpanded((isExpanded) => !isExpanded)}
                    sx={{ color: 'inherit', display: { xs: 'none', sm: 'inline-flex' } }}
                  >
                    {expanded ? (
                      <CloseFullscreenRoundedIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <OpenInFullRoundedIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Minimise">
                  <IconButton aria-label={`Minimise ${MAVEN.name}`} onClick={onClose} sx={{ color: 'inherit' }}>
                    <KeyboardArrowDownRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
                <Box sx={{ mb: 3 }}>
                  <MavenMessage>
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                      {MAVEN.greeting}
                    </Typography>
                    {items.length === 0 && clearedItems && (
                      <Fade in>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          <Typography variant="caption" sx={{ color: AIADMIN_TEXT_PRIMARY, opacity: 0.7 }}>
                            Started a new chat.
                          </Typography>
                          <Button size="small" onClick={undoNewChat} sx={{ minWidth: 0, px: 1, textTransform: 'none' }}>
                            Undo
                          </Button>
                        </Box>
                      </Fade>
                    )}
                    {items.length === 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, mt: 1.5 }}>
                        {MAVEN.examples.map((example) => (
                          <Chip key={example} label={example} variant="outlined" onClick={() => ask(example)} />
                        ))}
                      </Box>
                    )}
                  </MavenMessage>
                </Box>
                {items.map((item) => (
                  <Box key={item.id} sx={{ mb: 3 }}>
                    <UserMessage text={item.question} />
                    <MavenMessage>
                      {item.running && !item.streamText && <MavenWorking steps={item.steps} />}
                      {item.error && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          {item.error}
                        </Typography>
                      )}
                      {(item.answer || item.streamText) && !item.error && (
                        <Box sx={{ mt: 0.5 }}>
                          <MavenAnswer
                            answer={item.answer || item.streamText}
                            check={item.check}
                            streaming={Boolean(item.running)}
                          />
                        </Box>
                      )}
                    </MavenMessage>
                  </Box>
                ))}
                <div ref={listEndRef} />
              </Box>

              {/* Composer: one capsule with the send button inside, on a translucent bar. Instead of a hard
              divider, a short fade softens where the messages meet the bar. */}
              <Box
                component="form"
                onSubmit={onSubmit}
                sx={{
                  position: 'relative',
                  px: 2,
                  pt: 1.25,
                  bgcolor: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(16px) saturate(180%)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: -20,
                    height: 20,
                    pointerEvents: 'none',
                    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.9))'
                  },
                  '@media (prefers-reduced-transparency: reduce)': {
                    bgcolor: AIADMIN_SURFACE_WHITE_HEX_COLOR,
                    backdropFilter: 'none'
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 1,
                    pl: 2,
                    pr: 0.625,
                    py: 0.625,
                    borderRadius: '22px',
                    border: `1px solid ${AIADMIN_BORDER_DEFAULT_COLOR}`,
                    bgcolor: AIADMIN_SURFACE_WHITE_HEX_COLOR,
                    transition: 'border-color 150ms ease',
                    '&:focus-within': { borderColor: AIADMIN_PRIMARY_COLOR },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                  }}
                >
                  <InputBase
                    fullWidth
                    multiline
                    maxRows={5}
                    placeholder={`Message ${MAVEN.name}…`}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={onKeyDown}
                    inputRef={inputRef}
                    inputProps={{ 'aria-label': `Message ${MAVEN.name}`, maxLength: 500 }}
                    sx={{
                      py: 0.75,
                      fontSize: 14,
                      lineHeight: 1.45,
                      color: AIADMIN_TEXT_PRIMARY,
                      '& textarea::placeholder': { color: AIADMIN_SECONDARY_TEXT_COLOR, opacity: 0.8 }
                    }}
                  />
                  <IconButton
                    type="submit"
                    disabled={!canSend}
                    aria-label="Send message"
                    sx={{
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      bgcolor: AIADMIN_PRIMARY_COLOR,
                      color: AIADMIN_TEXT_SECONDARY,
                      // Feedback on press, not on release.
                      transition: 'transform 100ms ease-out, background-color 150ms ease',
                      '&:hover': { bgcolor: AIADMIN_PRIMARY_HOVER_COLOR },
                      '&:active': { transform: 'scale(0.92)' },
                      '&.Mui-disabled': { bgcolor: AIADMIN_BORDER_DEFAULT_COLOR, color: AIADMIN_TEXT_SECONDARY },
                      '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                        '&:active': { transform: 'none' }
                      }
                    }}
                  >
                    <ArrowUpwardRoundedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
                <Typography
                  variant="caption"
                  component="p"
                  sx={{
                    textAlign: 'center',
                    pt: 0.75,
                    pb: 1.25,
                    fontSize: 11.5,
                    letterSpacing: '0.01em',
                    color: AIADMIN_SECONDARY_TEXT_COLOR
                  }}
                >
                  {MAVEN.footer}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grow>
      </Box>
    </ThemeProvider>
  );
};

MavenDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  pageContext: PropTypes.shape({ page: PropTypes.string, graphId: PropTypes.string, version: PropTypes.number }),
  page: PropTypes.string,
  /** Distance from the bottom of the screen, in px (same as the Maven button). */
  bottom: PropTypes.number,
  /** Distance from the right of the screen, in px (same as the Maven button). */
  right: PropTypes.number
};

MavenDrawer.defaultProps = { pageContext: null, page: null, bottom: 24, right: 24 };

export default MavenDrawer;
